import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { RiErrorWarningLine } from "react-icons/ri";
import { GrCircleQuestion } from "react-icons/gr";
import ModalInfo from "../../../components/lib/modalInfo";
import { AiOutlineClose } from "react-icons/ai";
import CustomButton from "../../../components/lib/customButton";
import AssetsService from "../../../rest/assetsService";

const StudentEditForm = ({ isOpen, student, onClose, onSubmit, onDirty, hideFooter = false }) => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [removedImage, setRemovedImage] = useState(false);

    useEffect(() => {
        const loadImage = async () => {
            if (student?.image_url) {
                try {
                    const response = await AssetsService.getProfileImage(student.image_url);
                    setSelectedImage(response);
                    setRemovedImage(false);
                } catch (e) {
                    console.error("Erro ao carregar imagem do aluno", e);
                }
            } else {
                setSelectedImage(null);
            }
        };

        if (isOpen) loadImage();
    }, [student, isOpen]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: student?.name || "",
            email: student?.email || "",
            phone_number: student?.phone_number || "",
            emergency_value: student?.emergency_value ?? 0.0,
            spend_limit: student?.spend_limit ?? 0.0,
            isPayingStudent: student?.isPayingStudent ?? true,
            user_status: student?.user_status ?? false,
            profileImage: null,
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .required("Nome é um campo obrigatório")
                .max(50, "O nome deve ter no máximo 50 caracteres")
                .test(
                    "two-words",
                    "O nome deve ser completo, formado por mais de uma palavra.",
                    (value) => value && value.trim().split(/\s+/).length >= 2
                ),
            email: Yup.string()
                .email("Email inválido")
                .matches(
                    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    "O email não pode conter acentos ou caracteres especiais"
                ),
            profileImage: Yup.mixed()
                .nullable()
                .test("fileSize", "A foto deve ter no máximo 25MB", (value) => {
                    if (!value) return true;
                    return value.size <= 25 * 1024 * 1024;
                })
                .test("fileType", "Formato de imagem inválido", (value) => {
                    if (!value) return true;
                    return ["image/jpeg", "image/png", "image/jpg"].includes(value.type);
                }),
        }),
        onSubmit: async (values) => {
            try {
                let imageUrl = student.image_url;

                if (removedImage) {
                    imageUrl = "";
                } else if (values.profileImage) {
                    const formData = new FormData();
                    formData.append("file", values.profileImage);

                    const uploadResponse = await AssetsService.uploadStudentProfileImage(formData);
                    imageUrl = uploadResponse.data;
                }

                await onSubmit({
                    ...student,
                    ...values,
                    image_url: imageUrl,
                    client_code: student.client_code,
                });

            } catch (e) {
                console.error(e);
            }
        }
    });

    const handleImageChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setSelectedImage(URL.createObjectURL(file));
        formik.setFieldValue("profileImage", file);
        setRemovedImage(false);
        onDirty();
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => setIsDragging(false);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);

        const file = e.dataTransfer.files?.[0];
        if (!file) return;

        setSelectedImage(URL.createObjectURL(file));
        formik.setFieldValue("profileImage", file);
        setRemovedImage(false);
        onDirty();
    };

    return (
        <form
            data-step-form
            onSubmit={formik.handleSubmit}
            className="flex flex-col h-full "
            onChange={onDirty}
        >
            <div>
                <p className="mb-1 font-medium">Imagem do Aluno</p>

                <div
                    className={`flex items-center justify-center rounded-md border-2 border-dashed p-4 transition
        ${isDragging ? "ring-2 ring-custom_blue" : "border-gray-300"}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {selectedImage ? (
                        <div className="relative w-40 h-40">
                            <img
                                src={selectedImage}
                                alt="Preview"
                                className="w-full h-full object-cover rounded-md"
                            />
                            <button
                                type="button"
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                                onClick={() => {
                                    setSelectedImage(null);
                                    formik.setFieldValue("profileImage", null);
                                    setRemovedImage(true);
                                    onDirty();
                                }}
                            >
                                <AiOutlineClose size={14} />
                            </button>
                        </div>
                    ) : (
                        <label className="cursor-pointer text-center text-sm text-gray-500">
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleImageChange}
                            />
                            Clique ou arraste a imagem aqui
                        </label>
                    )}
                </div>
                {formik.errors.profileImage && (
                    <p className="text-red-500 text-xs">{formik.errors.profileImage}</p>
                )}
            </div>

            <label className="mt-4">
                <div className="flex items-center justify-between">
                    <p>Nome do Aluno</p>
                    <ModalInfo icon={<RiErrorWarningLine />} content="Campo Obrigatório!" />
                </div>
                <input
                    className={`block w-full px-3 py-1.5 border rounded ${formik.touched.name && formik.errors.name ? "border-red-500" : "border-gray-300"}`}
                    name="name"
                    type="text"
                    onChange={(e) => { onDirty(); formik.handleChange(e); }}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                />
                {formik.touched.name && formik.errors.name && (
                    <p className="text-red-500 text-xs">{formik.errors.name}</p>
                )}
            </label>

            <div className="grid grid-cols-2 gap-4 my-2">
                <label>
                    <p>Email</p>
                    <input
                        className={`block w-full px-3 py-1.5 border rounded ${formik.touched.email && formik.errors.email ? "border-red-500" : "border-gray-300"}`}
                        name="email"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                    />
                    {formik.touched.email && formik.errors.email && (
                        <p className="text-red-500 text-xs">{formik.errors.email}</p>
                    )}
                </label>

                <label>
                    <p>Telefone</p>
                    <input
                        className="block w-full px-3 py-1.5 border border-gray-300 rounded"
                        name="phone_number"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.phone_number}
                    />
                </label>
            </div>

            <div className="grid grid-cols-2 gap-4 my-2">
                <label>
                    <div className="flex items-center justify-between">
                        <p>Valor de Emergência</p>
                        <ModalInfo icon={<GrCircleQuestion />} content="Valor negativo máximo permitido." />
                    </div>
                    <input
                        className="block w-full px-3 py-1.5 border border-gray-300 rounded"
                        name="emergency_value"
                        type="number"
                        step="0.05"
                        onChange={formik.handleChange}
                        value={formik.values.emergency_value}
                    />
                </label>

                <label>
                    <div className="flex items-center justify-between">
                        <p>Limite de gastos diário</p>
                        <ModalInfo icon={<GrCircleQuestion />} content="Limite diário de consumo." />
                    </div>
                    <input
                        className="block w-full px-3 py-1.5 border border-gray-300 rounded"
                        name="spend_limit"
                        type="number"
                        step="0.05"
                        onChange={formik.handleChange}
                        value={formik.values.spend_limit}
                    />
                </label>
            </div>

            <div className="grid grid-cols-2 gap-4 my-2">
                <div className="flex items-center justify-center">
                    Aluno Pagante:
                    <input
                        className="mx-4 h-6 w-6"
                        name="isPayingStudent"
                        type="checkbox"
                        checked={formik.values.isPayingStudent}
                        onChange={formik.handleChange}
                    />
                    <ModalInfo icon={<GrCircleQuestion />} content="Define se o aluno consome crédito Foodz." />
                </div>

                <div className="flex items-center justify-center">
                    Aluno Ativo:
                    <input
                        className="mx-4 h-6 w-6"
                        name="user_status"
                        type="checkbox"
                        checked={formik.values.user_status}
                        onChange={formik.handleChange}
                    />
                    <ModalInfo icon={<GrCircleQuestion />} content="Define se o aluno está ativo ou não." />
                </div>
            </div>

            {!hideFooter && (
                <div className="mt-auto flex justify-end gap-3 pt-4">
                    <CustomButton
                        buttonType="cancel"
                        type="button"
                        text="Cancelar"
                        onClick={onClose}
                        fullWidth
                    />
                    <CustomButton
                        buttonType="save"
                        type="submit"
                        text="Salvar"
                        fullWidth
                    />
                </div>
            )}
        </form>
    );
};

export default StudentEditForm;
